/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * PR-2 / PR-5 (S585) -- the Text Speaker Live word balance.
 *
 * Endpoint:
 *   GET /portal/tsl-words/balance/?activation_key=<key>
 *
 * One service, called by both surfaces that show the figure: the dashboard
 * licence page and the Extra Words cart. A balance shown in two places is a
 * promise that both places show the same number, and the only way to keep that
 * promise is for both to be told it by the same code.
 *
 * `status` is read before `words_remaining`. UserData1 on the licence is not
 * always a number -- this is EX-10, open with the desktop team as item 41 --
 * and the server answers "unknown" rather than guessing. A component that read
 * the number first and treated a null as zero would tell a customer they had
 * spent every word they own.
 *
 * The balance falls as the customer works, so the cache is SHORT. It is
 * deliberately not shorter: the figure moves with the desktop application, not
 * with anything the browser does, so a tighter window would add requests
 * without adding accuracy.
 */

import { createApi } from "@reduxjs/toolkit/query/react";
import {
  baseQueryWithReauth,
  CACHE_TIMES,
  TAG_TYPES,
} from "@/redux/api-config";

// ── Types ───────────────────────────────────────────────────────────────────

/** "known" carries a figure. "unknown" carries none, and says so. */
export type WordBalanceStatus = "known" | "unknown";

export interface WordBalanceResponse {
  status: WordBalanceStatus;
  /** Null whenever `status` is "unknown". Never read without checking it. */
  words_remaining: number | null;
  /** "YYYY-MM-DD", or null when the licence carries no readable expiry. */
  renewal_date: string | null;
  /** True while purchased words are erased at renewal (PR-3). */
  expires_at_renewal: boolean;
  /** False for a trial. Extra Words cannot be added to one. */
  is_paid_plan: boolean;
  /** False once the licence has passed its expiry date. */
  is_current: boolean;
  /**
   * FD-1 (S599): false when the shop could not identify the plan on this
   * licence. Optional so an older server, which does not send it, reads as
   * recognised and the browser behaves exactly as it did before.
   */
  plan_recognised?: boolean;
  /**
   * PR-2 (S599): purchased words still held, and the date they expire.
   * Optional so an older server, which sends neither, simply renders no line.
   */
  purchased_words?: number;
  purchased_words_expires_at?: string | null;
}

// ── API ─────────────────────────────────────────────────────────────────────

export const wordBalanceApi = createApi({
  reducerPath: "wordBalanceApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [TAG_TYPES.WORD_BALANCE],
  keepUnusedDataFor: CACHE_TIMES.SHORT,
  endpoints: (builder) => ({
    getWordBalance: builder.query<WordBalanceResponse, string>({
      // Arg is the activation key.
      query: (activationKey) =>
        `/portal/tsl-words/balance/?activation_key=${encodeURIComponent(activationKey)}`,
      providesTags: (_result, _error, activationKey) => [
        { type: TAG_TYPES.WORD_BALANCE, id: activationKey },
        { type: TAG_TYPES.WORD_BALANCE, id: "BALANCE" },
      ],
      keepUnusedDataFor: CACHE_TIMES.SHORT,
    }),
  }),
});

export const { useGetWordBalanceQuery } = wordBalanceApi;
